import { useLanguage } from "@/contexts/LanguageContext";
import { TrendingUp, Users, Zap, Star } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";

// Import platform logos
import googleReviewsLogo from "@/assets/logos/google-reviews.png";
import instagramLogo from "@/assets/logos/instagram.webp";
import googleMapsLogo from "@/assets/logos/google-maps.png";

const AIVisibility = () => {
  const { language } = useLanguage();

  const chartData = [
    { month: "Jan", value: 45 },
    { month: "Fév", value: 58 },
    { month: "Mar", value: 72 },
    { month: "Avr", value: 85 },
    { month: "Mai", value: 95 },
    { month: "Juin", value: 110 },
  ];

  const maxValue = Math.max(...chartData.map(d => d.value));

  const topSources = [
    { 
      name: "Google Reviews", 
      value: 847, 
      subtitle: language === "fr" ? "Avis 5 étoiles" : "5-star reviews", 
      color: "from-amber-500 to-orange-600", 
      logo: googleReviewsLogo,
    },
    { 
      name: "Instagram", 
      value: 2340, 
      subtitle: language === "fr" ? "Nouveaux abonnés" : "New followers", 
      color: "from-purple-500 to-violet-600", 
      logo: instagramLogo,
    },
    { 
      name: "Google Maps", 
      value: 156, 
      subtitle: language === "fr" ? "Nouvelles découvertes" : "New discoveries", 
      color: "from-emerald-500 to-teal-600", 
      logo: googleMapsLogo,
    },
  ];

  return (
    <section id="engagement" className="py-10 lg:py-12 bg-slate-50/50">
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <AnimatedSection animation="fade-right">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-rose-100/60 to-violet-100/60 border border-rose-200/50 backdrop-blur-sm">
                <Zap className="h-4 w-4 text-rose-500" />
                <span className="text-sm font-medium bg-gradient-to-r from-rose-500 to-violet-500 bg-clip-text text-transparent">
                  {language === "fr" ? "Engagement nouvelle génération" : "Next-gen engagement"}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight text-slate-800">
                {language === "fr" ? "La fidélité client transforme votre business." : "Customer loyalty transforms your business."}
                <br />
                <span className="bg-gradient-to-r from-rose-400 via-pink-500 to-violet-500 bg-clip-text text-transparent animate-gradient-shine">
                  {language === "fr" ? "Prenez le contrôle." : "Take control."}
                </span>
              </h2>

              <p className="text-lg text-slate-500 leading-relaxed">
                {language === "fr" 
                  ? "Chaque jour, vos clients découvrent de nouveaux commerces. La question est : comment les encourager à revenir et vous recommander ? Localove transforme chaque visite en opportunité de fidélisation."
                  : "Every day, your customers discover new businesses. The question is: how do you encourage them to come back and recommend you? Localove transforms every visit into a loyalty opportunity."}
              </p>

              <div className="grid sm:grid-cols-3 gap-6 pt-4">
                {[
                  { 
                    icon: Star, 
                    title: language === "fr" ? "Avis clients" : "Reviews", 
                    desc: language === "fr" ? "Collecte automatisée" : "Automated collection" 
                  },
                  { 
                    icon: TrendingUp, 
                    title: language === "fr" ? "Croissance" : "Growth", 
                    desc: language === "fr" ? "Suivi en temps réel" : "Real-time tracking" 
                  },
                  { 
                    icon: Users, 
                    title: language === "fr" ? "Communauté" : "Community", 
                    desc: language === "fr" ? "Abonnés fidèles" : "Loyal followers" 
                  },
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className="group flex items-start gap-3 transform transition-all duration-300 hover:translate-x-1"
                    style={{ transitionDelay: `${index * 0.1}s` }}
                  >
                    <div className="p-2.5 rounded-xl border transition-all duration-300 group-hover:scale-110 bg-gradient-to-br from-rose-100/80 to-violet-100/80 border-rose-200/50 group-hover:shadow-lg group-hover:shadow-rose-500/20">
                      <item.icon className="h-5 w-5 text-rose-500" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-700">{item.title}</div>
                      <div className="text-sm text-slate-500">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* Right Dashboard Mockup */}
          <AnimatedSection animation="fade-left" delay={0.2}>
            <div className="space-y-6">
              {/* Chart Card */}
              <div className="group relative p-6 rounded-3xl card-highlight transition-all duration-500 overflow-hidden">
                {/* Glow effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute -inset-1 bg-gradient-to-r from-rose-500/15 via-pink-500/15 to-violet-500/15 blur-xl" />
                </div>
                
                {/* Glass shine effect */}
                <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none z-30">
                  <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
                </div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-display font-bold text-slate-800 mb-1 transition-colors duration-300 group-hover:text-rose-500">
                        {language === "fr" ? "Tendances d'engagement" : "Engagement Trends"}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {language === "fr" ? "Suivez vos interactions clients" : "Track your customer interactions"}
                      </p>
                    </div>
                    <div className="px-3 py-1 rounded-full bg-emerald-100/80 border border-emerald-200/50">
                      <span className="text-xs font-medium text-emerald-600">+144%</span>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="space-y-4">
                    <div className="flex items-end justify-between gap-3 h-48">
                      {chartData.map((data, index) => (
                        <div key={index} className="flex-1 flex flex-col items-center gap-2 group/bar">
                          <div className="w-full relative" style={{ height: '160px' }}>
                            <div
                              className="absolute bottom-0 w-full rounded-xl transition-all duration-500 group-hover/bar:scale-x-110"
                              style={{
                                height: `${(data.value / maxValue) * 100}%`,
                                background: `linear-gradient(180deg, rgba(244, 63, 94, 0.9), rgba(139, 92, 246, 0.7))`,
                                boxShadow: '0 0 20px rgba(244, 63, 94, 0.2)',
                              }}
                            />
                          </div>
                          <span className="text-xs text-slate-400 font-medium transition-colors duration-300 group-hover/bar:text-rose-500">
                            {data.month}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Sources Card */}
              <div className="group relative p-6 rounded-3xl card-highlight transition-all duration-500 overflow-hidden">
                {/* Glow effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute -inset-1 bg-gradient-to-r from-rose-500/15 via-pink-500/15 to-violet-500/15 blur-xl" />
                </div>
                
                {/* Glass shine effect */}
                <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none z-30">
                  <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
                </div>
                
                <div className="relative z-10">
                  <h3 className="text-lg font-display font-bold text-slate-800 mb-4 transition-colors duration-300 group-hover:text-rose-500">
                    {language === "fr" ? "Sources d'engagement" : "Engagement Sources"}
                  </h3>
                  <div className="space-y-3">
                    {topSources.map((source, index) => (
                      <div
                        key={index}
                        className="group/item flex items-center justify-between p-4 rounded-2xl bg-slate-50/80 border border-slate-200/50 hover:border-rose-300/50 hover:bg-white transition-all duration-300 hover:translate-x-1 cursor-pointer hover:shadow-md hover:shadow-rose-500/5"
                        style={{ transitionDelay: `${index * 0.05}s` }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm transition-all duration-300 group-hover/item:scale-110 group-hover/item:shadow-lg overflow-hidden p-1.5">
                            <img src={source.logo} alt={source.name} className="w-full h-full object-contain" />
                          </div>
                          <div>
                            <div className="font-semibold text-slate-700">{source.name}</div>
                            <div className="text-xs text-slate-400">
                              {source.subtitle}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-display font-bold bg-gradient-to-r ${source.color} bg-clip-text text-transparent`}>
                            {source.value.toLocaleString()}
                          </div>
                          <div className="text-xs text-slate-400">
                            {language === "fr" ? "ce mois" : "this month"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default AIVisibility;